import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { adminContext } from "../../Common/PageRoute";
import { FileDownloadStatus } from "../../Utilities/ApiUtility.axios";
import XcelifyExtractionService, {
  IXcelifyAdminListItem,
  IXcelifyItemKey,
  IIdNameOption,
} from "../../Services/XcelifyExtractionService";
import { usePractice } from "../../Context/PracticeContext";

export interface IPracticeOption {
  id: number;
  name: string;
}

export interface IExtractionProgressState {
  active: boolean;
  total: number;
  processed: number;
  percent: number;
}

interface ISavedJobSession {
  jobId: string;
  totalCount: number;
  startedAt: number;
}

const LOCAL_STORAGE_JOB_KEY = "xcelify_active_extraction_job";

const loadSavedJobSession = (): ISavedJobSession | null => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_JOB_KEY);
    if (!raw) return null;
    const data: ISavedJobSession = JSON.parse(raw);
    if (!data?.jobId || Date.now() - data.startedAt > 30 * 60 * 1000) {
      localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
      return null;
    }
    return data;
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
    return null;
  }
};

const saveJobSession = (jobId: string, totalCount: number) => {
  try {
    const session: ISavedJobSession = { jobId, totalCount, startedAt: Date.now() };
    localStorage.setItem(LOCAL_STORAGE_JOB_KEY, JSON.stringify(session));
  } catch { }
};

const clearSavedJobSession = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_JOB_KEY);
  } catch { }
};

export function useXcelifyExtraction() {
  const { openNotification } = useContext(adminContext);
  const { allPracticeIds } = usePractice();

  const practiceMap: Record<number, string> = {};
  allPracticeIds.forEach((p: any) => {
    practiceMap[p.value] = p.label;
  });

  // Status & Progress States
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Tenant & Filter States 
  const [companyList, setCompanyList] = useState<IIdNameOption[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [bankList, setBankList] = useState<IIdNameOption[]>([]);
  const [loadingBanks, setLoadingBanks] = useState<boolean>(false);
  const [selectedPracticeId, setSelectedPracticeId] = useState<number | undefined>(undefined);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(undefined);
  const [selectedBankId, setSelectedBankId] = useState<string | undefined>(undefined);

  // Pagination & Search States
  const [items, setItems] = useState<IXcelifyAdminListItem[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(15);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Table Row Selection
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedItems, setSelectedItems] = useState<IXcelifyItemKey[]>([]);

  // Extraction Progress State
  const [extractionProgress, setExtractionProgress] = useState<IExtractionProgressState>({
    active: false,
    total: 0,
    processed: 0,
    percent: 0,
  });

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const resetProgressUI = useCallback(() => {
    stopPolling();
    setDownloading(false);
    setActiveJobId(null);
    clearSavedJobSession();
    setTimeout(() => {
      setExtractionProgress({ active: false, total: 0, processed: 0, percent: 0 });
    }, 3000);
  }, [stopPolling]);

  // Restore Active Job Session on Mount
  useEffect(() => {
    const saved = loadSavedJobSession();
    if (!saved) return;

    setDownloading(true);
    setActiveJobId(saved.jobId);
    setExtractionProgress({ active: true, total: saved.totalCount, processed: 0, percent: 0 });

    stopPolling();
    pollTimerRef.current = setInterval(async () => {
      try {
        const prog = await XcelifyExtractionService.getProgress(saved.jobId);
        if (prog && prog.status !== "Not Found") {
          setExtractionProgress((prev) => ({
            active: true,
            total: prog.total || saved.totalCount || prev.total,
            processed: prog.processed ?? prev.processed,
            percent: prog.percent ?? prev.percent,
          }));

          const isTerminal =
            (prog.status === "Completed" && prog.processed >= prog.total && prog.total > 0) ||
            prog.status === "Failed" ||
            prog.status === "Cancelled";

          if (isTerminal) {
            resetProgressUI();
          }
        }
      } catch { }
    }, 1000);

    return () => stopPolling();
  }, [stopPolling, resetProgressUI]);

  // Fetch Practices List
  useEffect(() => {
    let isMounted = true;

    if (isMounted && allPracticeIds?.length > 0 && !selectedPracticeId) {
      setSelectedPracticeId(allPracticeIds[0].value);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPracticeIds]);

  // Fetch Companies by Practice ID
  useEffect(() => {
    if (!selectedPracticeId || selectedPracticeId <= 0) {
      setCompanyList([]);
      setSelectedClientId(undefined);
      return;
    }

    let isMounted = true;
    setLoadingCompanies(true);
    XcelifyExtractionService.getCompaniesByPractice(selectedPracticeId)
      .then((res) => {
        if (isMounted) setCompanyList(res || []);
      })
      .catch((err) => {
        console.error("Error loading practice companies:", err);
        if (isMounted) setCompanyList([]);
      })
      .finally(() => {
        if (isMounted) setLoadingCompanies(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedPracticeId]);

  // Fetch Banks by Practice ID
  useEffect(() => {
    if (!selectedPracticeId || selectedPracticeId <= 0) {
      setBankList([]);
      setSelectedBankId(undefined);
      return;
    }

    let isMounted = true;
    setLoadingBanks(true);
    XcelifyExtractionService.getBanksByPractice(selectedPracticeId)
      .then((res) => {
        if (isMounted) setBankList(res || []);
      })
      .catch((err) => {
        console.error("Error loading practice banks:", err);
        if (isMounted) setBankList([]);
      })
      .finally(() => {
        if (isMounted) setLoadingBanks(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedPracticeId]);

  // Fetch Xcelify Records
  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const start = (currentPage - 1) * pageSize;
      const res = await XcelifyExtractionService.getList(
        start,
        pageSize,
        searchQuery,
        selectedPracticeId || 0,
        selectedClientId,
        selectedBankId
      );
      if (res) {
        setItems(res.items ?? []);
        setTotalItems(res.totalRecords ?? 0);
      } else {
        openNotification("error", "Failed to fetch Xcelify records");
      }
    } catch (err: any) {
      openNotification("error", err?.message || "Error fetching Xcelify records");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, selectedPracticeId, selectedClientId, selectedBankId, openNotification]);

  useEffect(() => {
    if (selectedPracticeId === undefined) return;
    fetchRecords();
  }, [fetchRecords, refreshToggle, selectedPracticeId]);

  // Handlers
  const handlePracticeChange = useCallback((value?: number) => {
    setSelectedPracticeId(value);
    setSelectedClientId(undefined);
    setSelectedBankId(undefined);
    setCurrentPage(1);
  }, []);

  const handleCompanyChange = useCallback((value?: string) => {
    setSelectedClientId(value);
    setCurrentPage(1);
  }, []);

  const handleBankChange = useCallback((value?: string) => {
    setSelectedBankId(value);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback((val: string) => {
    setSearchQuery(val);
    setCurrentPage(1);
  }, []);

  const handleRefresh = useCallback(() => setRefreshToggle((p) => !p), []);

  const handleSingleDownload = useCallback(
    async (record: IXcelifyAdminListItem) => {
      setDownloadingId(record.id);
      try {
        openNotification("info", `Downloading statement ${record.number} (${record.file?.name || "statement.pdf"})...`);
        const res = await XcelifyExtractionService.downloadSingle(record.id, record.practiceId);
        if (res?.status === FileDownloadStatus.error) {
          openNotification("error", "Download failed. File not found or inaccessible.");
        }
      } catch (err: any) {
        openNotification("error", err?.message || "Failed to download single file.");
      } finally {
        setDownloadingId(null);
      }
    },
    [openNotification]
  );

  const handleCancelExtraction = useCallback(async () => {
    stopPolling();
    const jobIdToCancel = activeJobId || loadSavedJobSession()?.jobId;

    clearSavedJobSession();
    setDownloading(false);
    setActiveJobId(null);
    setExtractionProgress({ active: false, total: 0, processed: 0, percent: 0 });

    if (jobIdToCancel) {
      try {
        await XcelifyExtractionService.cancelProgress(jobIdToCancel);
      } catch (e) {
        console.error("Cancel API error:", e);
      }
    }

    openNotification("warning", "Extraction job cancelled by user.");
  }, [activeJobId, stopPolling, openNotification]);

  const handleBulkExtraction = useCallback(
    async (extractAll: boolean = false) => {
      if (!extractAll && selectedItems.length === 0) {
        openNotification("warning", "Please select at least one record to extract.");
        return;
      }

      const count = extractAll ? totalItems : selectedItems.length;
      const jobId = `job_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

      saveJobSession(jobId, count);
      setDownloading(true);
      setActiveJobId(jobId);
      setExtractionProgress({ active: true, total: count, processed: 0, percent: 0 });

      stopPolling();
      pollTimerRef.current = setInterval(async () => {
        try {
          const prog = await XcelifyExtractionService.getProgress(jobId);
          if (prog) {
            setExtractionProgress((prev) => ({
              active: true,
              total: prog.total || count || prev.total,
              processed: prog.processed ?? prev.processed,
              percent: prog.percent ?? prev.percent,
            }));

            const isTerminal =
              (prog.status === "Completed" && prog.processed >= prog.total && prog.total > 0) ||
              prog.status === "Failed" ||
              prog.status === "Cancelled";

            if (isTerminal) {
              stopPolling();
            }
          }
        } catch { }
      }, 1000);

      try {
        openNotification("info", `Streaming ${count} statement file(s) into zip package...`);
        const res = await XcelifyExtractionService.extractBulk(
          extractAll ? [] : selectedItems,
          extractAll,
          selectedPracticeId || 0,
          selectedClientId,
          selectedBankId,
          searchQuery,
          jobId
        );
        if (res?.status === FileDownloadStatus.error) {
          openNotification("error", "Extraction failed. Files could not be retrieved.");
        }
      } catch (err: any) {
        openNotification("error", err?.message || "Failed to extract bulk files.");
      } finally {
        resetProgressUI();
      }
    },
    [selectedItems, totalItems, selectedPracticeId, selectedClientId, selectedBankId, searchQuery, stopPolling, resetProgressUI, openNotification]
  );

  const handleRowSelectionChange = useCallback((keys: React.Key[], rows: IXcelifyAdminListItem[]) => {
    setSelectedRowKeys(keys);
    setSelectedItems(rows.map((row) => ({ id: row.id, practiceId: row.practiceId })));
  }, []);

  return {
    loading,
    downloading,
    downloadingId,
    activeJobId,
    allPracticeIds,
    practiceMap,
    companyList,
    loadingCompanies,
    bankList,
    loadingBanks,
    selectedPracticeId,
    selectedClientId,
    selectedBankId,
    items,
    totalItems,
    currentPage,
    pageSize,
    selectedRowKeys,
    extractionProgress,
    setCurrentPage,
    setPageSize,
    handlePracticeChange,
    handleCompanyChange,
    handleBankChange,
    handleSearch,
    handleRefresh,
    handleSingleDownload,
    handleBulkExtraction,
    handleCancelExtraction,
    handleRowSelectionChange,
  };
}
